import React, { memo, useEffect, useLayoutEffect, useRef } from "react";
import "./zone-menu.css";

import { usePreviewContext } from "../../contexts/preview-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useMessage } from "../../hooks/useMessage";
import { usePreviewBox } from "../../hooks/usePreviewBox";
import { useResizeObserver } from "../../hooks/useResizeObserver";
import { useStableCallback } from "../../hooks/useStableCallback";

const margin = 10;

export const ZoneMenu = memo(({ children }: { children?: React.ReactNode }) => {
	const { editor } = useAppContext();

	/** To position the zone menu */
	const zoneMenuRef = useRef<HTMLDivElement>(null);

	/** To measure content size */
	const zoneMenuContentRef = useRef<HTMLDivElement>(null);

	/** Main function to update zone menu position to zone */
	const updateZoneMenu = useStableCallback(
		(zone: eXpress.core.IZone | undefined, previewRect: eXpress.math.IRect | null) => {
			if (!zoneMenuRef.current) return;
			if (!zone) {
				zoneMenuRef.current.style.display = "none";
			} else {
				if (!zoneMenuContentRef.current || !previewRect) return;
				zoneMenuRef.current.style.display = "block";

				const dipToScreenTransform = previewContext.getSpreadToScreenTransform(
					zone?.spread,
					undefined,
				);
				if (!dipToScreenTransform) return;

				const outerBoundsRect = dipToScreenTransform.transformRect(zone.spreadOuterBounds);
				const zoneMenuContentRect = zoneMenuContentRef.current.getBoundingClientRect();

				const zoneBottomCenter = eXpress.math.point(
					outerBoundsRect.left + outerBoundsRect.width / 2,
					outerBoundsRect.top + outerBoundsRect.height + margin,
				);
				const bottomZoneMenuRect = eXpress.math.rect(
					zoneBottomCenter.left - zoneMenuContentRect.width / 2,
					zoneBottomCenter.top,
					zoneMenuContentRect.width,
					zoneMenuContentRect.height,
				);

				const zoneTopCenter = eXpress.math.point(
					outerBoundsRect.left + outerBoundsRect.width / 2,
					outerBoundsRect.top - margin,
				);
				const topZoneMenuRect = eXpress.math.rect(
					zoneTopCenter.left - zoneMenuContentRect.width / 2,
					zoneTopCenter.top - zoneMenuContentRect.height,
					zoneMenuContentRect.width,
					zoneMenuContentRect.height,
				);

				// Check if top or bottom should be used
				const previewRect2 = eXpress.math.rect(previewRect);
				const areaInViewOnTop = previewRect2.intersect(topZoneMenuRect).area();
				const areaInViewOnBottom = previewRect2.intersect(bottomZoneMenuRect).area();

				// Move inside the previewRect with a margin
				let zoneMenuRect = bottomZoneMenuRect;
				if (areaInViewOnTop > areaInViewOnBottom) {
					zoneMenuRect = topZoneMenuRect.movedInRange(previewRect2.shrink(margin));
				} else {
					zoneMenuRect = bottomZoneMenuRect.movedInRange(previewRect2.shrink(margin));
				}

				// Position at bottom center of bounding box
				zoneMenuRef.current.style.transform = `translate3d(${zoneMenuRect.left}px, ${zoneMenuRect.top}px, 0)`;
				zoneMenuRef.current.style.width = `${zoneMenuRect.width}px`;
				zoneMenuRef.current.style.height = `${zoneMenuRect.height}px`;
			}
		},
	);

	useResizeObserver(zoneMenuContentRef, () => {
		updateZoneMenu(editor.selection.getCurrentZone(), previewBox);
	});

	const previewContext = usePreviewContext();
	const { previewBox } = usePreviewBox();

	/** Position on mount or selection change */
	useLayoutEffect(() => {
		const zone = editor.selection.getCurrentZone(); // Currently use selected zone from core
		updateZoneMenu(zone, previewBox);
	}, [editor.selection, previewBox, previewContext, updateZoneMenu]);

	// Update on slideshow change (zooming, panning preview)
	useEffect(
		function updateZoneMenuOnTransformChange() {
			const slideShow = previewContext.slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			return slideShow.onTransformChange.subscribe(function onTransformChange() {
				const zone = editor.selection.getCurrentZone(); // Currently use selected zone from core
				updateZoneMenu(zone, previewBox);
			});
		},
		[editor.selection, previewBox, previewContext, updateZoneMenu],
	);

	/** Update on message that can change the zone (zone moved, resized, rotated, other selection) */
	useMessage(
		(message) => {
			// Because message is synchronous, we read the selected zone for editor
			const zone = editor.selection.getCurrentZone();

			if (
				eXpress.core.isSelectedZoneChanged(message) ||
				eXpress.core.isZoneAffected(message, zone)
			)
				updateZoneMenu(zone, previewBox);
		},
		{
			captureInteractiveMessages: true,
		},
	);

	return (
		<div ref={zoneMenuRef} className="zone-menu">
			<div ref={zoneMenuContentRef} className="zone-menu__content">
				{children}
			</div>
		</div>
	);
});
ZoneMenu.displayName = "ZoneMenu";
